import { motion } from "framer-motion";
import { Reveal, SectionTag, SectionTitle, StatusBadge, GoldButton, EASE } from "./shared";
import { IMG, YEIDA_SCHEME, YEIDA_HISTORY, YEIDA_ELIGIBILITY, YEIDA_VS_GYC } from "../data/site";
import { Landmark, BellRing, ExternalLink, CheckCircle2 } from "lucide-react";

export function YeidaPlots({ onAlert }) {
  return (
    <section id="yeida" className="relative py-28 lg:py-40 bg-[#0A0A0A] overflow-hidden" data-testid="yeida-section">
      <div className="absolute inset-0">
        <img src={IMG.expressway} alt="Yamuna Expressway" className="w-full h-full object-cover img-cinematic opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8">
        <SectionTag>Yamuna Authority Plots</SectionTag>
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-end mb-16">
          <SectionTitle>
            YEIDA Plots.<br />
            <span className="text-gold-gradient italic">Land Allotted by the Authority Itself.</span>
          </SectionTitle>
          <Reveal delay={0.15}>
            <p className="text-sm sm:text-base text-[#A1A1AA] leading-relaxed max-w-lg">
              The Yamuna Expressway Industrial Development Authority (YEIDA) is the Uttar Pradesh
              government body that plans and allots land along the expressway. Residential plots are
              released through periodic online schemes and allotted by computerised draw &mdash; the most
              direct route to authority-titled land in the airport influence zone.
            </p>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-5 gap-6 mb-16">
          <Reveal className="lg:col-span-3" delay={0.05}>
            <div className="card-lux p-7 lg:p-9 rounded-[2px] h-full" data-testid="yeida-latest-scheme">
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <Landmark size={18} strokeWidth={1.4} className="text-[#D4AF37]" />
                <span className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold">Latest Scheme &mdash; {YEIDA_SCHEME.code}</span>
                <span className="px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] border border-[#71717A]/40 text-[#A1A1AA] rounded-full">{YEIDA_SCHEME.status}</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-8">
                {YEIDA_SCHEME.stats.map((s, i) => (
                  <motion.div key={s.label} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}>
                    <p className="font-serif-lux text-3xl lg:text-4xl text-[#F5F5F0]">{s.value}</p>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mt-1">{s.label}</p>
                  </motion.div>
                ))}
              </div>
              <dl className="divide-y divide-[#D4AF37]/10">
                {YEIDA_SCHEME.facts.map(([k, v]) => (
                  <div key={k} className="grid sm:grid-cols-[140px_1fr] gap-1 sm:gap-6 py-3">
                    <dt className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]/80 pt-0.5">{k}</dt>
                    <dd className="text-[13px] text-[#A1A1AA] leading-relaxed">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </Reveal>

          <div className="lg:col-span-2 flex flex-col gap-6">
            <Reveal delay={0.12}>
              <div className="card-lux p-7 rounded-[2px]" data-testid="yeida-history">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-5">Scheme Track Record</p>
                <ol className="relative border-l border-[#D4AF37]/20 ml-1.5 space-y-5">
                  {YEIDA_HISTORY.map((h) => (
                    <li key={h.title} className="pl-5 relative">
                      <span className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-[#D4AF37]" />
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <span className="text-[10px] uppercase tracking-[0.25em] text-[#71717A]">{h.year}</span>
                        <StatusBadge status={h.status} />
                      </div>
                      <p className="text-sm text-[#F5F5F0]">{h.title}</p>
                      <p className="text-[12px] text-[#71717A]">{h.detail}</p>
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="card-lux p-7 rounded-[2px]" data-testid="yeida-eligibility">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold mb-5">Who Can Apply</p>
                <ul className="space-y-3">
                  {YEIDA_ELIGIBILITY.map((e) => (
                    <li key={e} className="flex gap-3 text-[13px] text-[#A1A1AA] leading-relaxed">
                      <CheckCircle2 size={15} strokeWidth={1.5} className="text-[#D4AF37] mt-0.5 shrink-0" />
                      {e}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.1}>
          <div className="border border-[#D4AF37]/15 rounded-[2px] overflow-hidden" data-testid="yeida-vs-gyc-table">
            <div className="grid grid-cols-3 bg-[#D4AF37]/[0.06] px-5 py-4 text-[10px] uppercase tracking-[0.25em]">
              <span className="text-[#71717A]">Consideration</span>
              <span className="text-[#F3E5AB]">YEIDA Authority Plot</span>
              <span className="text-[#F3E5AB]">Gaur Yamuna City Plot</span>
            </div>
            {YEIDA_VS_GYC.map(([k, a, b], i) => (
              <div key={k} className={`grid grid-cols-3 gap-4 px-5 py-4 text-[12px] sm:text-[13px] leading-relaxed ${i % 2 ? "bg-[#0D0D0D]" : "bg-[#111111]"}`}>
                <span className="text-[#D4AF37]/80 uppercase tracking-[0.15em] text-[10px] sm:text-[11px] pt-0.5">{k}</span>
                <span className="text-[#A1A1AA]">{a}</span>
                <span className="text-[#A1A1AA]">{b}</span>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-12 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            <div className="max-w-2xl">
              <p className="font-serif-lux text-2xl text-[#F5F5F0] mb-2">Never miss the next YEIDA draw.</p>
              <p className="text-sm text-[#A1A1AA] leading-relaxed">
                We track scheme announcements, brochure releases and draw dates, help you prepare a
                compliant application, and advise on post-allotment options &mdash; including comparing an
                authority plot against ready township inventory.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <GoldButton data-testid="yeida-alert-btn" onClick={onAlert}>
                <BellRing size={14} /> Get Scheme Alerts
              </GoldButton>
              <a
                href="https://www.yamunaexpresswayauthority.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="yeida-official-link"
                className="inline-flex items-center gap-2 px-7 py-3.5 text-[12px] font-semibold uppercase tracking-[0.22em] border border-[#D4AF37]/50 text-[#F3E5AB] hover:bg-[#D4AF37]/10 rounded-[2px] transition-colors"
              >
                Official Portal <ExternalLink size={14} />
              </a>
            </div>
          </div>
          <p className="mt-8 text-[11px] text-[#52525b] leading-relaxed max-w-4xl" data-testid="yeida-disclaimer">
            Radiance Realtors is an independent advisory and is not affiliated with YEIDA. Scheme figures are
            compiled from the RPS-10/2026 brochure and public reporting as of June 2026 and may change; applications
            are accepted only through the official YEIDA portal. Allotment is by lottery and is not assured.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
